'use client'

import React from 'react'
import { Discount } from '@/payload-types'
import { cn } from '@/utilities/cn'
import { useTranslations } from 'next-intl'
import { motion } from 'framer-motion'

interface Props {
  discounts: Discount[]
  basePrice: number
  priceBlock?: React.ReactNode
}

export const BulkDiscountTable: React.FC<Props> = ({ discounts, basePrice, priceBlock }) => {
  const t = useTranslations('shop')

  if (!discounts || discounts.length === 0) return null

  // Normalize tiers from all discounts
  const allTiers = discounts.flatMap((discount) => {
    if (discount.apply_as_volume_pricing && (discount as any).volumePricing) {
      return (discount as any).volumePricing.map((tier: any) => ({
        id: `${discount.id}-${tier.id}`,
        quantity: tier.quantity as number,
        percentage: tier.discount_percentage as number,
      }))
    } else {
      return [
        {
          id: String(discount.id),
          quantity: (discount.bulk_quantity || 0) as number,
          percentage: (discount.discount_percentage || 0) as number,
        },
      ]
    }
  })

  // Sort tiers by quantity
  const sortedTiers = allTiers.sort((a, b) => (a.quantity || 0) - (b.quantity || 0))

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
    }).format(price)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 overflow-hidden"
    >
      <div className="flex justify-between items-baseline border-b border-white/20 pb-4">
        <h3 className="text-xs font-mono font-semibold uppercase tracking-[0.15em] text-white/80">
          {t('volume_pricing')}
        </h3>
        {priceBlock}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/20">
              <th className="px-0 py-3 font-mono font-semibold text-white/85 uppercase tracking-[0.1em] text-xs text-center">
                {t('quantity')}
              </th>
              <th className="px-6 py-3 font-mono font-semibold text-white/85 uppercase tracking-[0.1em] text-xs">
                {t('discount')}
              </th>
              <th className="px-0 py-3 font-mono font-semibold text-white/85 uppercase tracking-[0.1em] text-xs text-right">
                {t('price_per_unit')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/15">
            {sortedTiers.map((tier, index) => {
              const percentage = tier.percentage || 0
              const discountedPrice = basePrice * (1 - percentage / 100)
              const isLast = index === sortedTiers.length - 1

              return (
                <tr key={tier.id} className="transition-colors hover:bg-white/5">
                  <td className="px-0 py-5 font-mono text-white/90 text-center">
                    {tier.quantity}+
                  </td>
                  <td className="px-6 py-5 text-green-400 font-mono font-semibold">
                    -{percentage}%
                  </td>
                  <td className="px-0 py-5 text-right">
                    <div className="flex flex-col items-end">
                      <span
                        className={cn('font-mono text-white/90', isLast && 'text-white font-bold')}
                      >
                        {formatPrice(discountedPrice)}
                      </span>
                      {isLast && (
                        <span className="text-[10px] uppercase tracking-tighter text-white/80 font-bold mt-1">
                          {t('best_value')}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}
